/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the component tree and displays a fallback UI
 * with download, copy to clipboard, and expandable error details.
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle, RefreshCw, Download, Copy, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui';
import { downloadErrorReport, generateErrorReport, type ErrorReportContext } from '@shared/utils/errorHandler';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  isExpanded: boolean;
  copied: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  private copyTimeoutId?: number;
  private lastHandledUrl: string = '';
  private lastResetTime: number = 0;
  private static readonly MIN_RESET_INTERVAL_MS = 500; // Prevent rapid resets

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isExpanded: false,
      copied: false,
    };

    // Store initial URL (full URL for better accuracy)
    if (typeof window !== 'undefined') {
      this.lastHandledUrl = window.location.href;
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidMount() {
    // Listen for route changes to clear errors when navigating away
    if (typeof window !== 'undefined') {
      window.addEventListener('popstate', this.handleRouteChange);
      // Also listen for pushState/replaceState (React Router uses these)
      this.patchHistoryMethods();
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  componentWillUnmount() {
    if (this.copyTimeoutId) {
      window.clearTimeout(this.copyTimeoutId);
    }
    // Clean up event listeners
    // Note: We intentionally don't clean up pushstate listener as it's a global patch
    // and other ErrorBoundary instances may still need it
    if (typeof window !== 'undefined') {
      window.removeEventListener('popstate', this.handleRouteChange);
      window.removeEventListener('pushstate', this.handleRouteChange);
    }
  }

  /**
   * Patch history methods to dispatch custom events
   *
   * Note: We only patch pushState, not replaceState, because:
   * - pushState is used for actual navigation (new route)
   * - replaceState is often used for state updates without navigation
   *   (e.g., updating URL params, scroll restoration) and would trigger
   *   unnecessary re-renders if we reset the error boundary on each call
   */
  private patchHistoryMethods = () => {
    // Listen for custom events dispatched by patched history methods
    window.addEventListener('pushstate', this.handleRouteChange);

    // Patch pushState if not already patched
    const originalPushState = history.pushState.bind(history);

    // Check if already patched (avoid double patching)
    if (!(history.pushState as any).__errorBoundaryPatched) {
      history.pushState = function(...args) {
        originalPushState(...args);
        window.dispatchEvent(new Event('pushstate'));
      };
      (history.pushState as any).__errorBoundaryPatched = true;
    }
    // Note: We intentionally do NOT patch replaceState to avoid triggering
    // unnecessary error boundary resets on state updates
  };

  /**
   * Handle route change - clear error if URL actually changed
   *
   * Guards against rapid resets to prevent re-render loops:
   * 1. Only reset if the URL actually changed (not just state updates)
   * 2. Enforce a minimum time between resets
   */
  handleRouteChange = () => {
    if (typeof window === 'undefined') return;

    const currentUrl = window.location.href;
    const now = Date.now();

    // Only reset if:
    // 1. We have an error to clear
    // 2. The URL actually changed
    // 3. Enough time has passed since last reset (prevents rapid cycling)
    if (
      this.state.hasError &&
      currentUrl !== this.lastHandledUrl &&
      now - this.lastResetTime >= ErrorBoundary.MIN_RESET_INTERVAL_MS
    ) {
      this.lastResetTime = now;
      this.lastHandledUrl = currentUrl;
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        isExpanded: false,
        copied: false,
      });
    } else {
      // Update tracked URL even if we didn't reset (for next comparison)
      this.lastHandledUrl = currentUrl;
    }
  };

  /**
   * Extract tab/view name from URL pathname
   * e.g., "/app/epg" → "EPG", "/organizations" → "Organizations"
   */
  getTabNameFromUrl = (): string => {
    if (typeof window === 'undefined') return 'Unknown';

    const pathname = window.location.pathname;
    const segments = pathname.split('/').filter(Boolean);

    if (segments.length === 0) return 'Home';

    // Get the last segment and capitalize it
    const lastSegment = segments[segments.length - 1];
    return lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1);
  };

  /**
   * Generate error report context
   */
  generateErrorContext = (): ErrorReportContext => {
    const { error, errorInfo } = this.state;

    return {
      tabName: this.getTabNameFromUrl(),
      error,
      errorMessage: error?.message,
      additionalInfo: {
        componentStack: errorInfo?.componentStack || 'No component stack available',
        errorName: error?.name,
        errorStack: error?.stack,
      },
    };
  };

  /**
   * Generate full error report text
   */
  generateFullReport = (): string => {
    return generateErrorReport(this.generateErrorContext());
  };

  /**
   * Handle copy to clipboard
   */
  handleCopy = async () => {
    try {
      const report = this.generateFullReport();
      await navigator.clipboard.writeText(report);
      this.setState({ copied: true });

      // Reset copied state after 2 seconds
      this.copyTimeoutId = window.setTimeout(() => {
        this.setState({ copied: false });
      }, 2000);
    } catch (err) {
      console.error('Failed to copy error report:', err);
    }
  };

  /**
   * Handle download error report
   */
  handleDownload = () => {
    downloadErrorReport(this.generateErrorContext());
  };

  /**
   * Toggle expanded state
   */
  toggleExpanded = () => {
    this.setState(prevState => ({ isExpanded: !prevState.isExpanded }));
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isExpanded: false,
      copied: false,
    });
  };

  render() {
    const { isExpanded, copied } = this.state;

    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex items-center justify-center h-full bg-dark-bg p-4 md:p-8">
          <div className="max-w-2xl w-full bg-dark-surface border border-dark-border/30 rounded-xl overflow-hidden">
            {/* Header */}
            <div className="p-4 md:p-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertCircle size={24} className="text-red-500 shrink-0" />
                <h2 className="text-lg font-semibold text-white">
                  Unexpected Error
                </h2>
              </div>

              <p className="text-sm text-dark-muted mb-4">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 mb-4">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={this.toggleExpanded}
                  className="bg-transparent border border-dark-border/30 hover:bg-white/10"
                  data-testid="error-boundary-expand-btn"
                >
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 mr-2" />
                  ) : (
                    <ChevronDown className="w-4 h-4 mr-2" />
                  )}
                  {isExpanded ? 'Hide' : 'Show'} Details
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={this.handleCopy}
                  className="bg-transparent border border-dark-border/30 hover:bg-white/10"
                  data-testid="error-boundary-copy-btn"
                >
                  {copied ? (
                    <Check className="w-4 h-4 mr-2 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4 mr-2" />
                  )}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={this.handleDownload}
                  className="bg-transparent border border-dark-border/30 hover:bg-white/10"
                  data-testid="error-boundary-download-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>

              {/* Primary Actions */}
              <div className="flex gap-3">
                <Button
                  variant="primary"
                  onClick={this.handleReset}
                  className="flex-1"
                  data-interaction-id="button-try-again"
                >
                  <RefreshCw size={16} className="mr-2" />
                  Try Again
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => window.location.reload()}
                  className="flex-1"
                  data-interaction-id="button-reload-page"
                >
                  Reload Page
                </Button>
              </div>
            </div>

            {/* Expandable Error Details */}
            {isExpanded && (
              <div className="bg-red-950/50 border-t border-dark-border/30 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-red-400 text-xs font-semibold uppercase tracking-wider">
                    Full Error Report
                  </span>
                  <Button
                    variant="secondary"
                    size="xs"
                    onClick={this.handleCopy}
                    className="bg-transparent hover:bg-white/10"
                    data-testid="error-boundary-details-copy-btn"
                  >
                    {copied ? (
                      <>
                        <Check className="w-3 h-3 text-green-400 mr-1" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3 mr-1" />
                        Copy All
                      </>
                    )}
                  </Button>
                </div>
                <pre
                  className="text-red-400 text-xs font-mono whitespace-pre-wrap break-words bg-black/30 rounded p-3 max-h-80 overflow-auto"
                  data-testid="error-boundary-details-content"
                >
                  {this.generateFullReport()}
                </pre>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
